import { DatePipe, isPlatformBrowser, NgFor, NgIf } from '@angular/common';
import {
  Component,
  DestroyRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import { interval, Subscription } from 'rxjs';
import { Course, Room } from '@tanzspass/shared';
import {
  RoomCourseBucket,
  TvScreenPayload,
  TvScreenService,
} from '../../services/tv-screen.service';
import { NightlyReloadScheduler } from '../nightly-reload';

@Component({
  selector: 'ts-infosystem-toolbar',
  templateUrl: './infosystem-toolbar.component.html',
  styleUrls: ['./infosystem-toolbar.component.css'],
  imports: [NgIf, NgFor, DatePipe],
})
export class InfosystemToolbarComponent implements OnInit, OnDestroy {
  @Input() room: Room;
  @Input() schoolId: number;
  rooms: Room[] = [];
  // Single-room course state lives here, not on `room`: the parent replaces the
  // `room` @Input with a fresh object every poll, which would wipe any course we
  // projected onto it. Owning it locally decouples it from that churn.
  currentCourse: Course | null = null;
  nextCourse: Course | null = null;
  timeUntilNextCourse = '';
  time = new Date();
  private timeInterval: Subscription;
  private reloadScheduler?: NightlyReloadScheduler;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private tvScreenSrv: TvScreenService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    // Course data now rides the shared TV poll — no per-room HTTP from here.
    // Single-room mode renders this room's own bucket; "all courses" mode renders
    // every room of the school.
    this.tvScreenSrv.payload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => this.applyPayload(payload));

    this.startClock();
  }

  private applyPayload(payload: TvScreenPayload | null): void {
    if (!payload || !this.room) {
      return;
    }

    if (this.room.showAllCoursesInToolbar) {
      this.rooms = payload.roomCourses
        .map((bucket) => this.withCourses(bucket.room, bucket))
        .filter((room) => room.currentCourse?.id || room.nextCourse?.id);
    } else {
      const bucket = payload.roomCourses.find((entry) => entry.room.id === this.room.id);
      this.currentCourse = bucket?.currentCourse ?? null;
      this.nextCourse = bucket?.upcomingCourses[0] ?? null;
      this.timeUntilNextCourse = this.nextCourse ? this.relativeStart(this.nextCourse) : '';
    }
  }

  /** Project a bucket's current/next course onto the room the template renders. */
  private withCourses(room: Room, bucket: RoomCourseBucket): Room {
    room.currentCourse = bucket.currentCourse;
    room.nextCourse = bucket.upcomingCourses[0] ?? null;
    room.timeUntilNextCourse = room.nextCourse ? this.relativeStart(room.nextCourse) : '';
    return room;
  }

  /** Human "in X minutes" until a course starts, against the current wall clock. */
  private relativeStart(course: Course): string {
    const now = dayjs();
    const currentTime = dayjs('1970-01-01T00:00:00+01:00')
      .add(now.hour(), 'hour')
      .add(now.minute(), 'minute');
    return dayjs(course.time).from(currentTime);
  }

  private startClock(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Anchor the daily self-heal reload to the already-ticking clock so it
      // fires even after long uptime / timer drift; localStorage guards against
      // a reload loop within the reload hour.
      this.reloadScheduler = new NightlyReloadScheduler(window.localStorage, () =>
        window.location.reload(),
      );
      this.timeInterval = interval(1000).subscribe(() => {
        this.time = new Date();
        this.reloadScheduler?.tick(this.time);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.timeInterval) {
      this.timeInterval.unsubscribe();
    }
  }
}
